import React, { useCallback, useEffect, useState } from 'react';
import { Calendar } from 'react-modern-calendar-datepicker';
import { FormattedMessage, useIntl } from 'react-intl';
import { getCalendarLocale } from './calendarLocaleUtils';
import moment from 'moment';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import { Close } from '@mui/icons-material';
import {
  getDateObjectFromMoment,
  getDateTimeObjectFromMoment,
  getHourStringFromMoment,
  getMomentFromDateObject,
  getPeriodFromTimeFilter,
  getTimeFilters,
} from './momentUtils';
import { Formik, useFormikContext } from 'formik';
import BeSafeContainer from 'components/commons/BeSafeContainer';
import BeSafeControlFeedback from 'components/commons/BeSafeControlFeedback';

const CalendarQuickButtons = ({ onChange, onReset, options }) => {
  const { values, dirty } = useFormikContext();

  const handleChange = (e, option) => {
    e.preventDefault();
    onChange(option);
  };

  return (
    <>
      <Row>
        <Col>
          <div
            className="d-flex flex-wrap"
            style={{
              padding: '1em',
              justifyContent: 'space-evenly',
              gap: '5px',
            }}
          >
            {options.map((option, index) => (
              <Button
                key={index}
                variant="outline-primary"
                className="py-1 px-1"
                active={values.quickButtonSelected === option.name}
                onClick={(e) => handleChange(e, option)}
              >
                <FormattedMessage id={option.displayName} />
              </Button>
            ))}
          </div>
        </Col>
        {dirty && (
          <Col xs={2} className="align-self-center">
            <Button
              variant="outline-danger"
              className="py-1 px-1"
              onClick={onReset}
              size="lg"
            >
              <Close />
            </Button>
          </Col>
        )}
      </Row>
    </>
  );
};

const CalendarTimeSelectors = (props) => {
  const { values, touched, errors, setValues } = useFormikContext();

  const intl = useIntl();

  const extractDateAndTime = (dateTime) => {
    if (!dateTime) return { date: '', time: '' };
    const date = dateTime.locale(intl.locale).format('ll');
    const time = getHourStringFromMoment(dateTime);
    return { date, time };
  };

  const fromDateTime = values.selectedRange?.from;
  const { date: fromDate, time: fromTime } = extractDateAndTime(fromDateTime);

  const toDateTime = values.selectedRange?.to;
  const { date: toDate, time: toTime } = extractDateAndTime(toDateTime);

  const isDateRangeSet = !!fromDateTime && !!toDateTime;

  const handleTimeChange = (e) => {
    if (!isDateRangeSet) return;

    const { name, value } = e.target;

    const currentRange = values.selectedRange;
    if (!currentRange || !currentRange[name]) return;

    const newTime = values.selectedRange[name].clone().set({
      hour: value.split(':')[0],
      minute: value.split(':')[1],
    });

    const newRange = {
      ...values.selectedRange,
      [name]: newTime,
    };

    setValues({
      selectedRange: newRange,
      quickButtonSelected: '',
    });
  };

  return (
    <>
      <BeSafeContainer visible={isDateRangeSet}>
        <Row>
          <Col>
            <Form.Group controlId="testTimeFrom">
              <Form.Label>
                <FormattedMessage id="TIME.FROM" /> {fromDate}{' '}
                <FormattedMessage id="TIME.AT" />
              </Form.Label>
              <Form.Control
                type="time"
                name="from"
                placeholder="00:00"
                onChange={handleTimeChange}
                value={fromTime}
                isInvalid={touched.selectedRange && errors.selectedRangeInvalid}
              />
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>
                <FormattedMessage id="TIME.TO" /> {toDate}{' '}
                <FormattedMessage id="TIME.AT" />
              </Form.Label>
              <Form.Control
                type="time"
                name="to"
                placeholder="00:00"
                onChange={handleTimeChange}
                value={toTime}
                isInvalid={touched.selectedRange && errors.selectedRangeInvalid}
              />
            </Form.Group>
          </Col>
        </Row>
        {errors.selectedRangeInvalid && (
          <Row>
            <Col>
              <BeSafeControlFeedback>
                <FormattedMessage id={errors.selectedRangeInvalid} />
              </BeSafeControlFeedback>
            </Col>
          </Row>
        )}
      </BeSafeContainer>
    </>
  );
};

const AdvancedFilterCalendarFilterFooter = ({ onReset, haveTime }) => {
  const { setValues, handleSubmit } = useFormikContext();
  const intl = useIntl();

  const timeFilters = getTimeFilters();

  const handlePeriodChange = useCallback(
    (period) => {
      const timeFilter = timeFilters.find((f) => f.name === period.name);
      if (!timeFilter) return;

      setValues({
        quickButtonSelected: period.name,
        selectedRange: timeFilter.value,
      });
    },
    [setValues, timeFilters]
  );

  return (
    <>
      <CalendarQuickButtons
        onReset={onReset}
        onChange={handlePeriodChange}
        options={timeFilters}
      />

      <BeSafeContainer visible={haveTime}>
        <CalendarTimeSelectors />
      </BeSafeContainer>

      <Row>
        <Col>
          <Button
            variant="primary"
            size="lg"
            onClick={handleSubmit}
            className="mt-3 w-100"
          >
            {intl.formatMessage({
              id: 'GENERAL.APPLY',
            })}
          </Button>
        </Col>
      </Row>
    </>
  );
};

const AdvancedFilterCalendarFilter = ({
  filterSchema,
  filterKey,
  onChange,
  value: currentSelectedRangeValue,
}) => {
  const [locale, setLocale] = useState();
  // const { options, haveTime } = filterSchema;

  const intl = useIntl();

  const now = getDateTimeObjectFromMoment(moment());
  const today = {
    year: now.year,
    month: now.month,
    day: now.day,
  };

  useEffect(() => {
    const calendarLocale = getCalendarLocale(intl);

    setLocale({ ...calendarLocale });
  }, [intl]);

  const deriveQuickButton = (range) => {
    if (!range?.from || !range?.to) return '';
    const parsed = {
      from: getDateTimeObjectFromMoment(range.from),
      to: getDateTimeObjectFromMoment(range.to),
    };
    return getPeriodFromTimeFilter(parsed) || '';
  };

  const initialValues = {
    selectedRange: {
      from: null,
      to: null,
      ...currentSelectedRangeValue,
    },
    quickButtonSelected: deriveQuickButton(currentSelectedRangeValue),
  };

  const handleDateChange = (date, { setValues }) => {
    const { from, to } = date;

    const parsedDate = {
      from: getMomentFromDateObject(from),
      to: getMomentFromDateObject(to)?.endOf('day'),
    };

    setValues({
      selectedRange: parsedDate,
      quickButtonSelected: '',
    });
  };

  const parseToCalendar = (values) => {
    if (!values) return { from: null, to: null };

    const { from, to } = values;
    const result = {
      from: from ? getDateObjectFromMoment(from) : null,
      to: to ? getDateObjectFromMoment(to) : null,
    };

    return result;
  };

  const validateForm = (values) => {
    const errors = {};

    const { selectedRange } = values;

    if (!selectedRange.from || !selectedRange.to) {
      errors.selectedRangeRequired = 'FILTERS.DATE.REQUIRED';
    } else if (
      selectedRange.from &&
      selectedRange.to &&
      selectedRange.from.isAfter(selectedRange.to, 'minute')
    ) {
      errors.selectedRangeInvalid = 'FILTERS.DATE.INVALID_RANGE';
    }

    return errors;
  };

  const handleSubmit = (values) => {
    const { selectedRange } = values;
    onChange(selectedRange);
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={validateForm}
        enableReinitialize
      >
        {({ errors, handleReset, values, setValues }) => (
          <Calendar
            value={parseToCalendar(values.selectedRange)}
            onChange={(date) => {
              handleDateChange(date, { setValues });
            }}
            maximumDate={today}
            colorPrimary={'#435986'}
            colorPrimaryLight={'hsl(220, 33%, 80%)'}
            locale={locale}
            shouldHighlightWeekends
            renderFooter={() => (
              <Container fluid className="px-3 pb-3">
                {errors.selectedRangeRequired && (
                  <Row>
                    <Col>
                      <BeSafeControlFeedback>
                        <FormattedMessage id={errors.selectedRangeRequired} />
                      </BeSafeControlFeedback>
                    </Col>
                  </Row>
                )}
                <AdvancedFilterCalendarFilterFooter
                  onReset={handleReset}
                  haveTime
                />
              </Container>
            )}
          />
        )}
      </Formik>
    </>
  );
};

export default AdvancedFilterCalendarFilter;
